/**
 * Scene ↔ DOM overlay bridge. Scenes open modals, overlays (OVL-xx) and full screens
 * (SCR-xx) and await their result; the Preact overlay renders whatever is on top of the
 * stack. Built-in kinds are typed in ModalMap; features register more kinds at runtime
 * (features/registry.ts) and open them with `openAny`. No Phaser or Preact imports here.
 */
import type { JSX } from 'preact';
import type { Grant, BoosterKey } from '../config/content.js';
import type { CustomResultLine } from '../services/customRound.js';
import type { RescueMethod } from '../services/rewards.js';

export type AdOfferUi = { kind: 'ad' } | { kind: 'gems'; gems: number } | { kind: 'tryLater' };

export interface WinProps {
  level: number;
  stars: number;
  newBest: boolean;
  reward: number;
  tips: number;
  sweep: number;
  xp: number;
  bestCombo: number;
  /** MO-01 double tips: the ad, or "try again soon" */
  doubleTips: 'ad' | 'tryLater';
  onDoubleTips: () => Promise<boolean>;
  /** SO-03: a 3-star win can challenge friends */
  canBeat: boolean;
  onBeat: () => Promise<void>;
  /** roadmap-3k shop finale (once per shop): the shop's name for the "big upgrade" banner; its button closes with 'shop' */
  finale?: string | null;
}

export interface FailProps {
  level: number;
  reason: 'bagFull' | 'timeout' | 'stuck';
  canMap: boolean;
}

/** The standard result screen of a feature-launched round (services/customRound.ts). */
export interface RoundResultProps {
  result: 'win' | 'fail';
  stars: number;
  reason: 'bagFull' | 'timeout' | 'stuck' | null;
  /** the feature's title (localised), else win/fail defaults */
  title: string | null;
  lines: CustomResultLine[];
  coins: number;
  retry: boolean;
}

export interface RescueProps {
  kind: 'slot' | 'time';
  reason: 'bagFull' | 'timeout' | 'stuck';
  /** the goal's sets still to pop (on a collectGoal level: ticket sets only, see goldenLeft) */
  setsLeft: number;
  /** collectGoal: golden items not yet popped (absent on every other level) */
  goldenLeft?: number;
  free: boolean;
  gems: number;
  canAffordGems: boolean;
  boosters: number;
  /** MO-01.2 rewarded rescue: the ad, or "try again soon" when there is none */
  ad: 'ad' | 'tryLater';
  /** the Rush (2L): a continue is a rewarded ad only, so the free, gem and booster rescues are not offered */
  adOnly?: boolean;
}

export interface ModalMap {
  win: { props: WinProps; result: 'continue' | 'shop' };
  fail: { props: FailProps; result: 'retry' | 'map' };
  roundResult: { props: RoundResultProps; result: 'continue' | 'retry' };
  rescue: { props: RescueProps; result: RescueMethod | 'decline' };
  pause: { props: { level: number; canMap: boolean }; result: 'resume' | 'restart' | 'map' };
  nameShop: { props: { current: string | null }; result: string | null };
  offline: { props: { coins: number; hours: number; ad: 'ad' | 'tryLater' | 'none' }; result: 'collect' | 'double' };
  daily: { props: { day: number; grant: Grant; streak: number; gifts: Grant[] }; result: 'claim' };
  levelUp: { props: { level: number; unlocks: string[] }; result: 'ok' | string };
  mechanic: { props: { mechanic: string }; result: void };
  /** roadmap-3k §5: specials the player knows, back for the first time in this city (a one-card reminder) */
  mechanicRemind: { props: { mechanics: string[] }; result: void };
  grandOpening: { props: { shopName: string; nextShop: string | null }; result: 'share' | 'close' };
  /** out of a booster: buy one for gems, or (2L) watch a rewarded ad for `adQty` of it when `ad` is offered */
  buyBooster: { props: { booster: BoosterKey; price: number; gems: number; ad?: boolean; adQty?: number }; result: boolean | 'ad' };
  error: { props: { message: string }; result: 'retry' };
}

export type ModalKind = keyof ModalMap;

export interface ModalEntry<K extends string = string> {
  id: number;
  kind: K;
  props: K extends ModalKind ? ModalMap[K]['props'] : unknown;
  resolve: (v: unknown) => void;
  /** performance.now() when it opened (UX timings are wall-clock) */
  openedAt: number;
}

/** A rail icon: a built-in name (calendar, trophy…), an atlas sprite id (`ico_*`) or a component. */
export type RailIcon = string | ((p: { size?: number }) => JSX.Element);

export interface RailEntry {
  id: string;
  label: string;
  icon: RailIcon;
  badge: number | boolean | null;
  testId: string;
  onOpen: () => void;
}

export interface MapHud {
  level: number;
  maxLevel: number;
  onPlay: () => void;
  onShop: () => void;
  shopHint: boolean;
  offline: boolean;
  /** feature entry points (daily puzzle, league, friends, spin, inbox…) */
  rail: RailEntry[];
}

export interface ShopHud {
  shopId: string;
  /** style currently previewed per slot */
  selected: Record<string, 'a' | 'b' | 'c'>;
  building: boolean;
  onSelect: (slot: string, style: 'a' | 'b' | 'c') => void;
  onBuy: (slot: string, style: 'a' | 'b' | 'c') => void;
  /** SO-01: get the rare item a slot needs */
  onRestock: (slot: string, rareItem: string) => void;
  onBack: () => void;
  onPlay: () => void;
  /** read-only visit of a friend's shop (SO-06) */
  visit: { owner: string; trophy: boolean } | null;
  /** shop decor: Decorate mode (absent while visiting or when the shop has no decor spots) */
  decorate?: { active: boolean; onToggle: () => void; onStore: () => void } | null;
}

export interface PlayHud {
  level: number;
  /** 'daily' shows the date instead of the level */
  label?: string;
  onPause: () => void;
  /**
   * A feature's own in-round HUD (duels: the opponent's live progress). It renders as the first
   * child of the overlay, so the playbar, every view layer, the toasts and the veil paint over
   * it, and it inherits the overlay's `pointer-events: none` — it never takes a tap. The feature
   * sets it on the live HUD once the play scene is up (`beforeStart`, by which time the scene has
   * set `play`) and clears it when its round ends:
   *
   *     ui.setHud({ play: { ...ui.hud.play!, extra } })
   *
   * The scene replaces `play` wholesale in `create()`, so a slot set before the scene is up is
   * lost, and a restart drops it (which for a duel means the duel is already left).
   */
  extra?: (() => JSX.Element | null) | null;
}

export interface HudState {
  mode: 'none' | 'map' | 'shop' | 'play';
  map: MapHud | null;
  shop: ShopHud | null;
  play: PlayHud | null;
  /** coin counter bump animation key */
  coinPulse: number;
  /** top bar shortcuts */
  onStore: (() => void) | null;
  onSettings: (() => void) | null;
}

export interface Toast {
  id: number;
  text: string;
}

type Listener = () => void;

export class UiBridge {
  modals: ModalEntry[] = [];
  hud: HudState = { mode: 'none', map: null, shop: null, play: null, coinPulse: 0, onStore: null, onSettings: null };
  toasts: Toast[] = [];
  /** loading veil shown between scenes */
  busy = false;
  /** an ad is loading or on screen (AdsService.setLoading): a visible veil, since loading can take seconds */
  adLoading = false;
  private seq = 0;
  private listeners = new Set<Listener>();
  /** observers of modal open/close (scenes pause the active-play clock) */
  onModalChange: ((open: boolean) => void) | null = null;
  /** dismiss actions by entry id (Modal onClose / Screen onBack), for the system back button */
  private backs = new Map<number, () => void>();

  /** Register an entry's back action; returns the unregister. */
  setBack(id: number, fn: () => void): () => void {
    this.backs.set(id, fn);
    return () => {
      if (this.backs.get(id) === fn) this.backs.delete(id);
    };
  }

  /**
   * System back (Android back button, Escape): dismisses the top view the way its own close
   * or back button would. True when a view was on screen (a view with no dismiss, like the win
   * screen, swallows it); false when none is open, so the caller handles back itself.
   */
  back(): boolean {
    const top = this.top;
    if (!top) return false;
    this.backs.get(top.id)?.();
    return true;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  open<K extends ModalKind>(kind: K, props: ModalMap[K]['props']): Promise<ModalMap[K]['result']> {
    return this.openAny<ModalMap[K]['result']>(kind, props);
  }

  /** Open any registered kind (feature screens and overlays). */
  openAny<R>(kind: string, props: unknown): Promise<R> {
    return new Promise((resolve) => {
      const entry: ModalEntry = { id: ++this.seq, kind, props: props as never, resolve: resolve as (v: unknown) => void, openedAt: performance.now() };
      this.modals = [...this.modals, entry];
      if (this.modals.length === 1) this.onModalChange?.(true);
      this.emit();
    });
  }

  close(id: number, result: unknown): void {
    const entry = this.modals.find((m) => m.id === id);
    if (!entry) return;
    this.modals = this.modals.filter((m) => m.id !== id);
    if (this.modals.length === 0) this.onModalChange?.(false);
    this.emit();
    entry.resolve(result);
  }

  /** Close every open view of a kind (e.g. a screen replaced by another). */
  closeKind(kind: string, result: unknown = undefined): void {
    for (const m of this.modals.filter((x) => x.kind === kind)) this.close(m.id, result);
  }

  get top(): ModalEntry | null {
    return this.modals[this.modals.length - 1] ?? null;
  }

  setHud(partial: Partial<HudState>): void {
    this.hud = { ...this.hud, ...partial };
    this.emit();
  }

  toast(text: string, ms = 2200): void {
    const t = { id: ++this.seq, text };
    this.toasts = [...this.toasts, t];
    this.emit();
    setTimeout(() => {
      this.toasts = this.toasts.filter((x) => x.id !== t.id);
      this.emit();
    }, ms);
  }

  setAdLoading(on: boolean): void {
    this.adLoading = on;
    this.emit();
  }

  setBusy(on: boolean): void {
    this.busy = on;
    this.emit();
  }

  /** Force a re-render (e.g. locale change, a sheet finished loading). */
  refresh(): void {
    this.emit();
  }
}
